import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { schemaApi, workspacesApi } from "@/api/client";
import type {
  JobMetadata,
  ResourceRef,
  SchemaRef,
  Workspace,
} from "@/types/api";
import { studioWorkspacePath } from "@/utils/studioRoute";

function extractScripts(job: JobMetadata): ResourceRef[] {
  const middlewares = job.pipeline?.middlewares;
  if (!middlewares || middlewares.length === 0) return [];
  return middlewares.map((mw) => ({
    service_name: mw.service_name,
    resource: mw.resource,
  }));
}

function extractInputOrigin(job: JobMetadata): ResourceRef | null {
  const source = job.pipeline?.source;
  if (!source) return null;
  return { service_name: source.service_name, resource: source.resource };
}

function extractSchema(job: JobMetadata): SchemaRef | null {
  const schema = job.pipeline?.schemas?.[0];
  if (!schema) return null;
  return { schema_id: schema.id, service_name: schema.service_name };
}

export function buildWorkspaceFromJob(
  job: JobMetadata,
): Partial<Workspace> & { name: string } {
  return {
    name: job.name,
    scripts: extractScripts(job),
    input_origin: extractInputOrigin(job),
    schema: extractSchema(job),
    pipeline: job.pipeline ? JSON.stringify(job.pipeline, null, 2) : null,
    deployed_job: job.name,
  };
}

export async function prefillInput(
  origin: ResourceRef,
): Promise<string | null> {
  try {
    const variants = await schemaApi.getSchema(
      origin.service_name,
      origin.resource,
    );
    if (!variants || variants.length === 0) return null;
    const filled = await schemaApi.fill(variants[0].schema);
    return JSON.stringify(filled, null, 2);
  } catch {
    return null;
  }
}

export async function findWorkspaceForJob(
  jobName: string,
): Promise<Workspace | null> {
  const summaries = await workspacesApi.list();
  const candidates = summaries.filter((s) => s.name === jobName);

  if (candidates.length === 0) return null;

  const results = await Promise.all(
    candidates.map((s) => workspacesApi.get(s.id).catch(() => null)),
  );

  return results.find((ws) => ws?.deployed_job === jobName) ?? null;
}

export function useImportJob() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const importJob = useCallback(
    async (job: JobMetadata) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setLoading(true);
      setError(null);
      try {
        const existing = await findWorkspaceForJob(job.name);
        if (existing) {
          navigate(studioWorkspacePath(existing.id));
          return;
        }

        const payload = buildWorkspaceFromJob(job);
        if (payload.input_origin) {
          payload.input = await prefillInput(payload.input_origin);
        }
        const created = await workspacesApi.create(payload);
        navigate(studioWorkspacePath(created.id));
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to import job";
        setError(message);
      } finally {
        inFlight.current = false;
        setLoading(false);
      }
    },
    [navigate],
  );

  const dismissError = useCallback(() => setError(null), []);

  return { importJob, loading, error, dismissError };
}

// "Duplicate as draft" off a deployed pipeline on the Pipelines page (Figma
// 14894-157281 / 15013-126334): a workspace copy of the job's running
// config under the name the modal asks for, opened in the Studio as the
// draft it is. The copy points at no job — it is a new pipeline.
export function useDuplicateJob() {
  const navigate = useNavigate();
  const [pending, setPending] = useState<JobMetadata | null>(null);

  const duplicate = useCallback(
    async (name: string) => {
      if (!pending) return;
      const payload = {
        ...buildWorkspaceFromJob(pending),
        name,
        deployed_job: null,
      };
      if (payload.input_origin) {
        payload.input = await prefillInput(payload.input_origin);
      }
      const created = await workspacesApi.create(payload);
      setPending(null);
      navigate(studioWorkspacePath(created.id));
    },
    [pending, navigate],
  );

  return {
    pending,
    request: setPending,
    close: () => setPending(null),
    duplicate,
  };
}
